import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import Dialog from '@app/src/components/Dialog'
import TextField from '@app/src/components/Ui/TextField'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import type { AssessmentQuestionnaireSyncStatus, Question } from '@app/src/types/resourceExplorer'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { NotificationSeverity } from '@app/src/wf-constants'
import { Alert, CircularProgress, MenuItem, Stack, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

type SyncAssessmentWithQuestionnaireDialogProps = {
  assessmentTemplateId: number
  assessmentType?: AssessmentType
  open: boolean
  onClose: () => void
}

type AssessmentQuestionnaireSyncRequest = {
  addedQuestions: { questionId: number; sectionId: number | null }[]
}

const questionName = (question: Question) => question.kpiName || question.questionText

const SyncAssessmentWithQuestionnaireDialog: React.FC<SyncAssessmentWithQuestionnaireDialogProps> = ({
  assessmentTemplateId,
  assessmentType,
  open,
  onClose,
}) => {
  const { formatMessage } = useIntl()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const queryClient = useQueryClient()
  const [sectionByQuestionId, setSectionByQuestionId] = useState<Record<number, number>>({})
  const { mutate: syncAssessment, isPending: isSyncing } = useCreateResource<
    AssessmentQuestionnaireSyncStatus,
    AssessmentQuestionnaireSyncRequest
  >()

  const {
    data: syncStatus,
    isLoading,
    isError,
  } = useFetchResource<AssessmentQuestionnaireSyncStatus>({
    key: [FetchKey.AssessmentTemplateQuestionnaireSyncStatus, assessmentTemplateId],
    endpoint: endpoints.assessmentTemplateQuestionnaireSyncStatus(assessmentTemplateId),
    options: {
      enabled: open && Boolean(assessmentTemplateId),
    },
  })

  const defaultSectionId = syncStatus?.sections[0]?.id
  const showSectionSelect =
    assessmentType === AssessmentType.Flexible && (syncStatus?.sections.length ?? 0) > 1 && !syncStatus?.isSynced
  const needsRepair = Boolean(
    syncStatus?.isSynced && (syncStatus.maximumScoreIsStale || syncStatus.tagsAreStale || syncStatus.linksAreStale),
  )

  useEffect(() => {
    if (!syncStatus || defaultSectionId === undefined) return
    setSectionByQuestionId(
      Object.fromEntries(syncStatus.addedQuestions.map(question => [question.id, defaultSectionId])),
    )
  }, [syncStatus, defaultSectionId])

  const onSync = () => {
    syncAssessment(
      {
        url: endpoints.assessmentTemplateQuestionnaireSync(assessmentTemplateId),
        body: {
          addedQuestions:
            syncStatus?.addedQuestions.map(question => ({
              questionId: question.id,
              sectionId: sectionByQuestionId[question.id] ?? defaultSectionId ?? null,
            })) ?? [],
        },
      },
      {
        onError: error => {
          showErrorNotification({ requestError: error })
          queryClient.invalidateQueries({ queryKey: [FetchKey.AssessmentTemplateQuestionnaireSyncStatus] })
        },
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [FetchKey.AssessmentTemplateQuestionnaireSyncStatus] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.AssessmentTemplateWithClassificationRules] })
          showSnackbar({
            message: formatMessage({ id: 'assessments.sync.success' }),
            severity: NotificationSeverity.success,
          })
          onClose()
        },
      },
    )
  }

  const renderContent = () => {
    if (isError) {
      return <Alert severity="error">{formatMessage({ id: 'assessments.sync.fetchError' })}</Alert>
    }

    if (isLoading || !syncStatus) {
      return (
        <Stack alignItems="center" justifyContent="center" py={4}>
          <CircularProgress />
        </Stack>
      )
    }

    if (syncStatus.isSynced) {
      return needsRepair ? (
        <Alert severity="info">{formatMessage({ id: 'assessments.sync.repairNeeded' })}</Alert>
      ) : (
        <Typography>{formatMessage({ id: 'assessments.sync.allSynced' })}</Typography>
      )
    }

    return (
      <Stack spacing={3}>
        <Alert severity="info">
          <Typography variant="body2">{formatMessage({ id: 'assessments.sync.consequencesTitle' })}</Typography>
          <Stack component="ul" spacing={0.5} sx={{ my: 1, pl: 3 }}>
            <Typography component="li" variant="body2">
              {formatMessage({ id: 'assessments.sync.consequenceAddedRules' })}
            </Typography>
            <Typography component="li" variant="body2">
              {formatMessage({ id: 'assessments.sync.consequenceRemovals' })}
            </Typography>
            <Typography component="li" variant="body2">
              {formatMessage({ id: 'assessments.sync.consequenceMaxScore' })}
            </Typography>
            <Typography component="li" variant="body2">
              {formatMessage({ id: 'assessments.sync.consequenceReEvaluation' })}
            </Typography>
          </Stack>
        </Alert>
        <Alert severity="warning">{formatMessage({ id: 'assessments.sync.classifyFirstWarning' })}</Alert>
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: 'assessments.sync.oldResponsesNote' })}
        </Typography>
        {Boolean(syncStatus.addedQuestions.length) && (
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              {formatMessage({ id: 'assessments.sync.questionsToAdd' }, { count: syncStatus.addedQuestions.length })}
            </Typography>
            {syncStatus.addedQuestions.map(question => (
              <Stack key={question.id} direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Typography>{questionName(question)}</Typography>
                {showSectionSelect && (
                  <TextField
                    select
                    size="small"
                    name={`section-${question.id}`}
                    label={formatMessage({ id: 'assessments.sync.sectionLabel' })}
                    value={sectionByQuestionId[question.id] ?? defaultSectionId ?? ''}
                    onChange={event =>
                      setSectionByQuestionId(current => ({
                        ...current,
                        [question.id]: Number(event.target.value),
                      }))
                    }
                    StackProps={{ sx: { minWidth: 180 } }}
                  >
                    {syncStatus.sections.map(section => (
                      <MenuItem key={section.id} value={section.id}>
                        {section.name || String(section.id)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            ))}
          </Stack>
        )}
        {Boolean(syncStatus.removedQuestions.length) && (
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              {formatMessage(
                { id: 'assessments.sync.questionsToRemove' },
                { count: syncStatus.removedQuestions.length },
              )}
            </Typography>
            {syncStatus.removedQuestions.map(question => (
              <Typography key={question.id}>{questionName(question)}</Typography>
            ))}
          </Stack>
        )}
      </Stack>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={formatMessage({ id: 'assessments.sync.dialogTitle' })}
      content={renderContent()}
      buttons={[
        {
          label: formatMessage({ id: 'general.cancel' }),
          onClick: onClose,
          disabled: isSyncing,
        },
        {
          label: formatMessage({ id: 'assessments.sync.syncAction' }),
          variant: 'contained',
          onClick: onSync,
          loading: isSyncing,
          disabled: isLoading || !syncStatus || (syncStatus.isSynced && !needsRepair),
        },
      ]}
    />
  )
}

export default SyncAssessmentWithQuestionnaireDialog
